import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import {
  ArrowsClockwise, DownloadSimple, CircleNotch, FunnelSimple, Eye, CaretUp, CaretDown,
  CaretLeft, CaretRight, WarningCircle, Check, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAL = "#0E7C86";
const AMBER = "#F79009";
const SLATE = "#344054";

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The Audit Log opens on the current calendar month, 1st to today.
export const defaultFilters = () => {
  const today = new Date();
  return {
    sites: [], movement_type: "", status: "", requester: "", material: "", warehouse: "",
    gac_id: "", source: "",
    date_from: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`,
    date_to: isoDay(today),
    month: "",
  };
};

const EMPTY_FILTERS = {
  sites: [], movement_type: "", status: "", requester: "", material: "", warehouse: "",
  gac_id: "", source: "", date_from: "", date_to: "", month: "",
};

const money = (value, currency) => {
  if (value === null || value === undefined) return "—";
  const sign = Number(value) < 0 ? "-" : "";
  const formatted = Math.abs(Number(value)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "INR" ? `${sign}₹${formatted}` : `${sign}${formatted}${currency ? ` ${currency}` : ""}`;
};
const compact = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const qty = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// A bucket with nothing SAP-valued in it shows a dash - never a 0.00 that
// would read as "SAP says this cost nothing".
const valuedMoney = (value, valuedCount, currency) => (valuedCount ? money(value, currency) : "—");

// What each valuation state MEANS - a value SAP has not released is never
// shown as zero, it is shown as the reason it is missing.
export const VALUATION_BADGE = {
  retrieved: { label: "SAP valued", className: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  pending_authorisation: { label: "Pending SAP authorisation", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  no_valuation: { label: "No SAP value posted", className: "bg-[#F9FAFB] text-[#475467] border-[#E4E7EC]" },
  mixed_currency: { label: "Mixed currency - excluded", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  error: { label: "SAP read failed", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  pending: { label: "Not fetched yet", className: "bg-[#F9FAFB] text-[#475467] border-[#E4E7EC]" },
};

const STATUS_LABEL = {
  posted: { label: "Posted", className: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  failed: { label: "Failed", className: "bg-[#FEE4E2] text-[#B42318] border-[#FECDCA]" },
  pending: { label: "Posting...", className: "bg-[#FEF0C7] text-[#93370D] border-[#FEDF89]" },
  unknown: { label: "Verify in SAP", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
};

const SOURCE_LABEL = { cost_center: "Cost centre", physical_inventory: "Physical inventory" };

const toQuery = (filters, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(","));
    } else if (value !== "" && value !== null && value !== undefined) {
      params.set(key, value);
    }
  });
  return params.toString();
};

const KpiCard = ({ label, value, sub, testId, accent }) => (
  <div className="border border-[#E4E7EC] rounded-xl bg-white p-4" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-[#667085] font-semibold">{label}</p>
    <p className="text-xl font-bold mt-1.5" style={{ color: accent || "#101828" }}>{value}</p>
    {sub && <p className="text-[11px] text-[#667085] mt-1">{sub}</p>}
  </div>
);

const SearchableSelect = ({ value, options, placeholder, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal h-9 text-xs" data-testid={testId}>
          <span className={value ? "text-[#101828] truncate" : "text-[#98A2B3]"}>{value || placeholder}</span>
          <FunnelSimple size={13} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[260px]" align="start">
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => { onChange(""); setOpen(false); }} data-testid={`${testId}-any`}>
                <span className="text-[#667085]">{placeholder}</span>
              </CommandItem>
              {options.map((option) => (
                <CommandItem key={option} value={option} onSelect={() => { onChange(option); setOpen(false); }} data-testid={`${testId}-option-${option}`}>
                  {option}
                  {value === option && <Check size={13} className="ml-auto text-[#0E7C86]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const SiteMultiSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = (site) => onChange(value.includes(site) ? value.filter((s) => s !== site) : [...value, site]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal h-9 text-xs" data-testid="audit-filter-sites">
          <span className={value.length ? "text-[#101828]" : "text-[#98A2B3]"}>
            {value.length ? `${value.length} site(s): ${value.join(", ")}` : "All sites"}
          </span>
          <FunnelSimple size={13} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-[220px]" align="start">
        <div className="space-y-1 max-h-64 overflow-auto">
          {options.map((site) => (
            <button
              key={site}
              type="button"
              onClick={() => toggle(site)}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs hover:bg-[#F9FAFB]"
              data-testid={`audit-filter-site-${site}`}
            >
              <span className="font-medium text-[#344054]">{site}</span>
              {value.includes(site) && <Check size={13} className="text-[#0E7C86]" />}
            </button>
          ))}
          {!options.length && <p className="text-xs text-[#98A2B3] px-2 py-1">No sites yet.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ReconciliationStrip = ({ reconciliation, onSync, syncing }) => {
  const r = reconciliation || {};
  const scope = r.financial_scope_sites || [];
  return (
    <div className="border border-[#E4E7EC] rounded-xl bg-white px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="audit-reconciliation-strip">
      <div className="text-xs">
        <span className="text-[#667085]">Posted movements </span>
        <span className="font-bold text-[#101828]" data-testid="recon-posted">{compact(r.posted_transactions)}</span>
      </div>
      <div className="text-xs">
        <span className="text-[#667085]">Valued by SAP </span>
        <span className="font-bold text-[#027A48]" data-testid="recon-valued">{compact(r.values_retrieved)}</span>
      </div>
      <div className="text-xs">
        <span className="text-[#667085]">Awaiting value </span>
        <span className="font-bold text-[#B54708]" data-testid="recon-pending">{compact(r.pending_valuation)}</span>
      </div>
      {!!r.reconciliation_errors && (
        <div className="text-xs">
          <span className="text-[#667085]">Read errors </span>
          <span className="font-bold text-[#B42318]" data-testid="recon-errors">{compact(r.reconciliation_errors)}</span>
        </div>
      )}
      <div className="text-xs text-[#667085]" data-testid="recon-last-sync">
        Last SAP sync {r.last_sap_sync ? new Date(r.last_sap_sync).toLocaleString() : "never"}
      </div>
      {!!scope.length && (
        <div className="text-xs text-[#667085]" data-testid="recon-scope">
          SAP releases values for <span className="font-semibold text-[#344054]">{scope.join(", ")}</span>
        </div>
      )}
      <Button size="sm" variant="outline" className="h-8 text-xs ml-auto" onClick={onSync} disabled={syncing} data-testid="audit-sync-values-btn">
        {syncing ? <CircleNotch size={13} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={13} className="mr-1.5" />}
        Sync SAP values
      </Button>
    </div>
  );
};

const FilterPanel = ({ draft, setDraft, options, onApply, onReset, onClear, loading }) => {
  const applyOnEnter = (e) => { if (e.key === "Enter") onApply(); };
  return (
  <div className="border border-[#E4E7EC] rounded-xl bg-white p-4" data-testid="audit-filter-panel">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <div>
        <Label className="text-[11px] text-[#667085]">Site</Label>
        <SiteMultiSelect value={draft.sites} options={options.sites || []} onChange={(sites) => setDraft({ ...draft, sites })} />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Movement type</Label>
        <Select value={draft.movement_type || "all"} onValueChange={(v) => setDraft({ ...draft, movement_type: v === "all" ? "" : v })}>
          <SelectTrigger className="h-9 text-xs" data-testid="audit-filter-movement-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="audit-filter-movement-all">All movements</SelectItem>
            <SelectItem value="issue" data-testid="audit-filter-movement-issue">Goods Issue</SelectItem>
            <SelectItem value="return" data-testid="audit-filter-movement-return">Goods Return</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Requester</Label>
        <SearchableSelect value={draft.requester} options={options.requesters || []} placeholder="All requesters"
          onChange={(requester) => setDraft({ ...draft, requester })} testId="audit-filter-requester" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Warehouse / area</Label>
        <SearchableSelect value={draft.warehouse} options={options.warehouses || []} placeholder="All warehouses"
          onChange={(warehouse) => setDraft({ ...draft, warehouse })} testId="audit-filter-warehouse" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Material</Label>
        <Input className="h-9 text-xs" placeholder="Code or description" value={draft.material}
          onChange={(e) => setDraft({ ...draft, material: e.target.value })} onKeyDown={applyOnEnter} data-testid="audit-filter-material" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">SAP movement / GAC ID</Label>
        <Input className="h-9 text-xs" placeholder="e.g. 304947" value={draft.gac_id}
          onChange={(e) => setDraft({ ...draft, gac_id: e.target.value })} onKeyDown={applyOnEnter} data-testid="audit-filter-gac" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Posting status</Label>
        <Select value={draft.status || "all"} onValueChange={(v) => setDraft({ ...draft, status: v === "all" ? "" : v })}>
          <SelectTrigger className="h-9 text-xs" data-testid="audit-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(options.statuses || []).map((s) => (
              <SelectItem key={s} value={s} data-testid={`audit-filter-status-${s}`}>{STATUS_LABEL[s]?.label || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Posted from</Label>
        <Select value={draft.source || "all"} onValueChange={(v) => setDraft({ ...draft, source: v === "all" ? "" : v })}>
          <SelectTrigger className="h-9 text-xs" data-testid="audit-filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Both sources</SelectItem>
            <SelectItem value="cost_center" data-testid="audit-filter-source-cost-center">Cost centre consumption</SelectItem>
            <SelectItem value="physical_inventory" data-testid="audit-filter-source-pi">Physical inventory</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Date from</Label>
        <Input type="date" className="h-9 text-xs" value={draft.date_from}
          onChange={(e) => setDraft({ ...draft, date_from: e.target.value, month: "" })} data-testid="audit-filter-date-from" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Date to</Label>
        <Input type="date" className="h-9 text-xs" value={draft.date_to}
          onChange={(e) => setDraft({ ...draft, date_to: e.target.value, month: "" })} data-testid="audit-filter-date-to" />
      </div>
      <div>
        <Label className="text-[11px] text-[#667085]">Or whole month</Label>
        <Input type="month" className="h-9 text-xs" value={draft.month}
          onChange={(e) => setDraft({ ...draft, month: e.target.value, date_from: "", date_to: "" })} data-testid="audit-filter-month" />
      </div>
      <div className="flex items-end gap-2">
        <Button size="sm" className="h-9 text-xs bg-[#0E7C86] hover:bg-[#0A5D65]" onClick={onApply} disabled={loading} data-testid="audit-apply-filters-btn">
          {loading ? <CircleNotch size={13} className="animate-spin mr-1.5" /> : <FunnelSimple size={13} className="mr-1.5" />}Apply
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={onReset} data-testid="audit-reset-filters-btn">Reset</Button>
        <Button size="sm" variant="ghost" className="h-9 text-xs text-[#667085]" onClick={onClear} data-testid="audit-clear-filters-btn">
          <X size={13} className="mr-1" />Clear all
        </Button>
      </div>
    </div>
    <p className="text-[11px] text-[#667085] mt-3">
      Dates use SAP&apos;s posting date wherever SAP has confirmed one, and this app&apos;s submission time otherwise.
    </p>
  </div>
  );
};

const SiteSummaryTable = ({ sites, totals, currency, onPickSite, activeSites }) => (
  <div className="border border-[#E4E7EC] rounded-xl bg-white overflow-hidden" data-testid="audit-site-summary">
    <div className="px-4 py-3 border-b border-[#E4E7EC]">
      <h3 className="text-sm font-bold text-[#101828]">Site-wise summary</h3>
      <p className="text-[11px] text-[#667085]">Click a site to filter the whole dashboard by it.</p>
    </div>
    <Table>
      <TableHeader>
        <TableRow className="bg-[#F9FAFB]">
          <TableHead className="text-[11px]">Site</TableHead>
          <TableHead className="text-[11px] text-right">Goods issue value</TableHead>
          <TableHead className="text-[11px] text-right">Goods return value</TableHead>
          <TableHead className="text-[11px] text-right">Net consumption</TableHead>
          <TableHead className="text-[11px] text-right">Issues</TableHead>
          <TableHead className="text-[11px] text-right">Returns</TableHead>
          <TableHead className="text-[11px] text-right">Awaiting SAP value</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sites.map((site) => (
          <TableRow
            key={site.site_id}
            className={`cursor-pointer hover:bg-[#F9FAFB] ${activeSites.includes(site.site_id) ? "bg-[#F0F9FA]" : ""}`}
            onClick={() => onPickSite(site.site_id)}
            data-testid={`audit-site-row-${site.site_id}`}
          >
            <TableCell className="text-xs font-bold text-[#101828]">{site.site_id}</TableCell>
            <TableCell className="text-xs text-right">{valuedMoney(site.issue_value, site.valued_issue_count, currency)}</TableCell>
            <TableCell className="text-xs text-right">{valuedMoney(site.return_value, site.valued_return_count, currency)}</TableCell>
            <TableCell className="text-xs text-right font-semibold">
              {valuedMoney(site.net_value, site.valued_issue_count + site.valued_return_count, currency)}
            </TableCell>
            <TableCell className="text-xs text-right">{compact(site.issue_count)}</TableCell>
            <TableCell className="text-xs text-right">{compact(site.return_count)}</TableCell>
            <TableCell className="text-xs text-right">
              {site.unvalued_transactions
                ? <span className="text-[#B54708] font-semibold" data-testid={`audit-site-unvalued-${site.site_id}`}>{site.unvalued_transactions}</span>
                : <span className="text-[#98A2B3]">—</span>}
            </TableCell>
          </TableRow>
        ))}
        {!sites.length && (
          <TableRow><TableCell colSpan={7} className="text-xs text-[#98A2B3] text-center py-6">No movements match these filters.</TableCell></TableRow>
        )}
        {!!sites.length && (
          <TableRow className="bg-[#F9FAFB] font-bold" data-testid="audit-site-total-row">
            <TableCell className="text-xs">Total</TableCell>
            <TableCell className="text-xs text-right">{valuedMoney(totals.issue_value, totals.valued_issue_count, currency)}</TableCell>
            <TableCell className="text-xs text-right">{valuedMoney(totals.return_value, totals.valued_return_count, currency)}</TableCell>
            <TableCell className="text-xs text-right">
              {valuedMoney(totals.net_value, (totals.valued_issue_count || 0) + (totals.valued_return_count || 0), currency)}
            </TableCell>
            <TableCell className="text-xs text-right">{compact(totals.issue_count)}</TableCell>
            <TableCell className="text-xs text-right">{compact(totals.return_count)}</TableCell>
            <TableCell className="text-xs text-right">{totals.unvalued_transactions || "—"}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    {!!totals.unvalued_transactions && (
      <p className="text-[11px] text-[#B54708] px-4 py-2 border-t border-[#E4E7EC] bg-[#FFFAEB]" data-testid="audit-site-incomplete-note">
        <WarningCircle size={12} className="inline mr-1" />
        {totals.unvalued_transactions} posted movement(s) have no SAP value yet, so these totals are incomplete.
      </p>
    )}
  </div>
);

const ChartCard = ({ title, subtitle, children, testId, action }) => (
  <div className="border border-[#E4E7EC] rounded-xl bg-white p-4" data-testid={testId}>
    <div className="flex items-start justify-between gap-2 mb-3">
      <div>
        <h3 className="text-sm font-bold text-[#101828]">{title}</h3>
        {subtitle && <p className="text-[11px] text-[#667085]">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const SortHeader = ({ label, field, sort, onSort, align = "left" }) => (
  <TableHead className={`text-[11px] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
    onClick={() => onSort(field)} data-testid={`audit-sort-${field}`}>
    <span className="inline-flex items-center gap-1">
      {label}
      {sort.by === field && (sort.dir === "desc" ? <CaretDown size={10} /> : <CaretUp size={10} />)}
    </span>
  </TableHead>
);

export const InventoryMovementAuditDashboard = ({ onOpenDetail, refreshKey }) => {
  const [draft, setDraft] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [options, setOptions] = useState({ sites: [], requesters: [], warehouses: [], statuses: [] });
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendMetric, setTrendMetric] = useState("value");
  const [table, setTable] = useState({ rows: [], total: 0, page: 1, page_count: 1, page_size: 25 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState({ by: "date", dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => toQuery(filters), [filters]);

  const loadTable = useCallback(async () => {
    const q = toQuery(filters, { page, page_size: pageSize, sort_by: sort.by, sort_dir: sort.dir });
    const { data } = await axios.get(`${API}/cost-center-consumption/audit?${q}`);
    setTable(data);
  }, [filters, page, pageSize, sort]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, trendRes] = await Promise.all([
        axios.get(`${API}/cost-center-consumption/audit/summary?${query}`),
        axios.get(`${API}/cost-center-consumption/audit/trend?${toQuery({ ...filters, date_from: "", date_to: "", month: "" }, { months: 12 })}`),
      ]);
      setSummary(summaryRes.data);
      setTrend(trendRes.data);
      await loadTable();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the movement audit.");
    } finally {
      setLoading(false);
    }
  }, [query, filters, loadTable]);

  useEffect(() => {
    axios.get(`${API}/cost-center-consumption/audit/filters`).then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [query, refreshKey]);
  useEffect(() => { loadTable().catch(() => {}); /* eslint-disable-next-line */ }, [page, pageSize, sort]);

  const apply = () => { setPage(1); setFilters({ ...draft }); };
  const reset = () => { const d = defaultFilters(); setDraft(d); setPage(1); setFilters(d); };
  const clearAll = () => { setDraft({ ...EMPTY_FILTERS }); setPage(1); setFilters({ ...EMPTY_FILTERS }); };

  const pickSite = (siteId) => {
    const sites = filters.sites.includes(siteId) ? filters.sites.filter((s) => s !== siteId) : [...filters.sites, siteId];
    setDraft({ ...draft, sites });
    setPage(1);
    setFilters({ ...filters, sites });
  };

  const sortBy = (field) => {
    setPage(1);
    setSort((prev) => (prev.by === field ? { by: field, dir: prev.dir === "desc" ? "asc" : "desc" } : { by: field, dir: "desc" }));
  };

  const syncValues = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/cost-center-consumption/audit/sync-valuations`, { limit: 100 });
      toast.success(`SAP sync: ${data.retrieved} valued, ${data.pending} still awaiting a value.`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP financial report is unavailable right now.");
    } finally {
      setSyncing(false);
    }
  };

  const exportRows = async (format) => {
    setExporting(true);
    try {
      const q = toQuery(filters, { sort_by: sort.by, sort_dir: sort.dir });
      const { data } = await axios.get(`${API}/cost-center-consumption/audit/export?${q}`);
      if (!data.rows.length) { toast.error("Nothing to export for these filters."); return; }
      const aoa = [data.columns, ...data.rows.map((row) => data.columns.map((c) => row[c] ?? ""))];
      const sheet = XLSX.utils.aoa_to_sheet(aoa);
      const name = `Movement_Audit_${filters.date_from || filters.month || "all"}`;
      if (format === "csv") {
        const csv = XLSX.utils.sheet_to_csv(sheet);
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url; link.download = `${name}.csv`; link.click();
        URL.revokeObjectURL(url);
      } else {
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Movement Audit");
        XLSX.writeFile(book, `${name}.xlsx`);
      }
      toast.success(`Exported ${data.rows.length} movement(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const kpis = summary?.kpis || {};
  const currency = kpis.currency && kpis.currency !== "MIXED" ? kpis.currency : (kpis.currencies?.[0] || "INR");
  const siteChart = (summary?.sites || []).map((s) => ({ site: s.site_id, issue: s.issue_value, return: s.return_value }));
  const topMaterials = summary?.top_materials || [];

  return (
    <div className="space-y-5" data-testid="movement-audit-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#101828]">Inventory Movement Audit &amp; Financial Dashboard</h2>
          <p className="text-xs text-[#667085] mt-0.5">
            Cost-centre consumption and physical-inventory movements this portal posted to SAP, with the financial value read back
            from SAP&apos;s own accounting documents. NPD project issues are reported on the NPD pages.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => exportRows("csv")} disabled={exporting} data-testid="audit-export-csv-btn">
            <DownloadSimple size={13} className="mr-1.5" />CSV
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => exportRows("xlsx")} disabled={exporting} data-testid="audit-export-excel-btn">
            {exporting ? <CircleNotch size={13} className="animate-spin mr-1.5" /> : <DownloadSimple size={13} className="mr-1.5" />}Excel
          </Button>
        </div>
      </div>

      <FilterPanel draft={draft} setDraft={setDraft} options={options} onApply={apply} onReset={reset} onClear={clearAll} loading={loading} />

      <ReconciliationStrip reconciliation={summary?.reconciliation} onSync={syncValues} syncing={syncing} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="audit-kpis">
        <KpiCard testId="kpi-issue-value" label="Goods issue value" value={valuedMoney(kpis.issue_value, kpis.valued_issue_count, currency)}
          sub={kpis.valued_issue_count ? `${compact(kpis.valued_issue_count)} of ${compact(kpis.issue_count)} posted issue(s) valued by SAP` : `${compact(kpis.issue_count)} posted issue(s) - SAP released no value`}
          accent={TEAL} />
        <KpiCard testId="kpi-return-value" label="Goods return value" value={valuedMoney(kpis.return_value, kpis.valued_return_count, currency)}
          sub={kpis.valued_return_count ? `${compact(kpis.valued_return_count)} of ${compact(kpis.return_count)} posted return(s) valued by SAP` : `${compact(kpis.return_count)} posted return(s) - SAP released no value`}
          accent={AMBER} />
        <KpiCard testId="kpi-net-value" label="Net material consumption"
          value={valuedMoney(kpis.net_value, (kpis.valued_issue_count || 0) + (kpis.valued_return_count || 0), currency)}
          sub={(kpis.valued_issue_count || kpis.valued_return_count) ? "Issues less returns, SAP-confirmed only" : "No SAP-confirmed value in this view"} />
        <KpiCard testId="kpi-issue-count" label="Issue transactions" value={compact(kpis.issue_count)} sub="Posted in SAP" />
        <KpiCard testId="kpi-return-count" label="Return transactions" value={compact(kpis.return_count)} sub="Posted in SAP" />
        <div className="border border-[#E4E7EC] rounded-xl bg-white p-4" data-testid="kpi-quantities">
          <p className="text-[11px] uppercase tracking-wide text-[#667085] font-semibold">Quantity moved</p>
          {(kpis.quantities || []).length ? (
            <div className="mt-1.5 space-y-0.5">
              {kpis.quantities.map((q) => (
                <p key={q.uom} className="text-[11px] text-[#344054]" data-testid={`kpi-quantity-${q.uom}`}>
                  <span className="font-bold text-[#101828]">{qty(q.issue_quantity)}</span> {q.uom} out
                  <span className="text-[#667085]"> · {qty(q.return_quantity)} back</span>
                </p>
              ))}
            </div>
          ) : <p className="text-xl font-bold mt-1.5 text-[#101828]">—</p>}
        </div>
      </div>

      {!!kpis.unvalued_transactions && (
        <p className="text-xs text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2" data-testid="audit-unvalued-banner">
          <WarningCircle size={13} className="inline mr-1.5" />
          {kpis.unvalued_transactions} posted movement(s) carry no SAP value yet and are excluded from the amounts above
          {summary?.reconciliation?.pending_authorisation ? ` - ${summary.reconciliation.pending_authorisation} of them because SAP has not authorised this integration to read financial data for their company.` : "."}
        </p>
      )}

      <SiteSummaryTable sites={summary?.sites || []} totals={summary?.totals || {}} currency={currency}
        onPickSite={pickSite} activeSites={filters.sites} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Issue vs return by site" subtitle="SAP-confirmed values under the current filters" testId="audit-chart-sites">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={siteChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" vertical={false} />
              <XAxis dataKey="site" tick={{ fontSize: 11, fill: "#667085" }} />
              <YAxis tick={{ fontSize: 11, fill: "#667085" }} width={70} />
              <Tooltip formatter={(v) => money(v, currency)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="issue" name="Goods issue" fill={TEAL} radius={[4, 4, 0, 0]} onClick={(d) => pickSite(d.site)} cursor="pointer" />
              <Bar dataKey="return" name="Goods return" fill={AMBER} radius={[4, 4, 0, 0]} onClick={(d) => pickSite(d.site)} cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Monthly consumption trend"
          subtitle={trend ? `${trend.series?.[0]?.label} to ${trend.series?.[trend.series.length - 1]?.label} - this chart keeps its own 12-month window` : "Last 12 months"}
          testId="audit-chart-trend"
          action={
            <Select value={trendMetric} onValueChange={setTrendMetric}>
              <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="audit-trend-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="value" data-testid="audit-trend-metric-value">SAP value</SelectItem>
                <SelectItem value="count" data-testid="audit-trend-metric-count">Transactions</SelectItem>
              </SelectContent>
            </Select>
          }
        >
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trend?.series || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#667085" }} interval={0} angle={-30} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#667085" }} width={70} />
              <Tooltip formatter={(v) => (trendMetric === "value" ? money(v, currency) : compact(v))} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {trendMetric === "value" ? (
                <>
                  <Bar dataKey="issue_value" name="Issue value" fill={TEAL} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="return_value" name="Return value" fill={AMBER} radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="net_value" name="Net consumption" stroke={SLATE} strokeWidth={2} dot={{ r: 3 }} />
                </>
              ) : (
                <>
                  <Bar dataKey="issue_count" name="Issues" fill={TEAL} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="return_count" name="Returns" fill={AMBER} radius={[4, 4, 0, 0]} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-[#667085] mt-2" data-testid="audit-trend-note">
            SAP only releases amounts for the open fiscal period, so closed months show the movements and quantities this portal
            recorded, with values from the month SAP valuations were first cached onwards.
          </p>
        </ChartCard>
      </div>

      <ChartCard title="Top 10 materials by consumption value" subtitle="Net of returns, from SAP's accounting items" testId="audit-chart-materials">
        {topMaterials.length ? (
          <ResponsiveContainer width="100%" height={Math.max(220, topMaterials.length * 34)}>
            <BarChart data={topMaterials} layout="vertical" margin={{ top: 5, right: 24, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#667085" }} />
              <YAxis type="category" dataKey="material_id" width={110} tick={{ fontSize: 11, fill: "#344054" }} />
              <Tooltip formatter={(v, _n, item) => [money(v, currency), item?.payload?.description || "Net consumption"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="net_value" name="Net value" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => { setDraft({ ...draft, material: d.material_id }); setPage(1); setFilters({ ...filters, material: d.material_id }); }}>
                {topMaterials.map((m) => <Cell key={m.material_id} fill={m.net_value < 0 ? AMBER : TEAL} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-[#98A2B3] py-8 text-center" data-testid="audit-no-materials">
            No SAP-valued material lines under these filters yet.
          </p>
        )}
      </ChartCard>

      <div className="border border-[#E4E7EC] rounded-xl bg-white overflow-hidden" data-testid="audit-log-table-card">
        <div className="px-4 py-3 border-b border-[#E4E7EC] flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-[#101828]">Detailed movement log</h3>
            <p className="text-[11px] text-[#667085]" data-testid="audit-table-count">
              {compact(table.total)} movement(s) · page {table.page} of {table.page_count}
            </p>
          </div>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="audit-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)} data-testid={`audit-page-size-${n}`}>{n} per page</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <SortHeader label="Date" field="date" sort={sort} onSort={sortBy} />
                <TableHead className="text-[11px]">SAP posting date</TableHead>
                <SortHeader label="Site" field="site" sort={sort} onSort={sortBy} />
                <TableHead className="text-[11px]">Type</TableHead>
                <SortHeader label="Requester" field="requester" sort={sort} onSort={sortBy} />
                <TableHead className="text-[11px]">Material</TableHead>
                <TableHead className="text-[11px]">Warehouse</TableHead>
                <SortHeader label="Lines" field="lines" sort={sort} onSort={sortBy} align="right" />
                <SortHeader label="Movement value" field="amount" sort={sort} onSort={sortBy} align="right" />
                <TableHead className="text-[11px]">Valuation</TableHead>
                <TableHead className="text-[11px]">SAP document</TableHead>
                <SortHeader label="Status" field="status" sort={sort} onSort={sortBy} />
                <TableHead className="text-[11px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.rows.map((row) => {
                const valuation = VALUATION_BADGE[row.valuation_status] || VALUATION_BADGE.pending;
                const status = STATUS_LABEL[row.status] || { label: row.status, className: "bg-[#F9FAFB] text-[#475467] border-[#E4E7EC]" };
                return (
                  <TableRow key={row.id} className="hover:bg-[#F9FAFB]" data-testid={`audit-row-${row.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`audit-row-posting-date-${row.id}`}>{row.sap_posting_date || "—"}</TableCell>
                    <TableCell className="text-xs font-semibold">{row.site_id}</TableCell>
                    <TableCell className="text-xs">
                      <span className={row.movement_type === "issue" ? "text-[#0E7C86] font-semibold" : "text-[#B54708] font-semibold"}>
                        {row.movement_type === "issue" ? "Issue" : "Return"}
                      </span>
                      <span className="block text-[10px] text-[#98A2B3]">{SOURCE_LABEL[row.source]}</span>
                    </TableCell>
                    <TableCell className="text-xs">{row.requester_name || "—"}</TableCell>
                    <TableCell className="text-xs">{row.material || "—"}</TableCell>
                    <TableCell className="text-xs">{row.warehouse || "—"}</TableCell>
                    <TableCell className="text-xs text-right">{row.line_count}</TableCell>
                    <TableCell className="text-xs text-right font-semibold" data-testid={`audit-row-value-${row.id}`}>
                      {row.valuation_status === "retrieved" ? money(row.value_amount, row.currency) : "—"}
                    </TableCell>
                    <TableCell>
                      {row.status === "posted" ? (
                        <Badge variant="outline" className={`text-[10px] ${valuation.className}`} data-testid={`audit-row-valuation-${row.id}`}>
                          {valuation.label}
                        </Badge>
                      ) : <span className="text-xs text-[#98A2B3]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.sap_gac_id ? (
                        <div>
                          <span className="font-mono text-[11px] text-[#101828]">{row.sap_gac_id}</span>
                          {!!row.material_document_numbers.length && (
                            <span className="block font-mono text-[10px] text-[#667085]">{row.material_document_numbers.join(", ")}</span>
                          )}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${status.className}`}>{status.label}</Badge>
                      {row.needs_correction && (
                        <span className="block text-[10px] text-[#B42318] mt-0.5" data-testid={`audit-row-correction-${row.id}`}>Needs correction</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpenDetail(row)} data-testid={`audit-view-${row.id}`}>
                        <Eye size={13} className="mr-1" />View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!table.rows.length && (
                <TableRow><TableCell colSpan={13} className="text-xs text-[#98A2B3] text-center py-8" data-testid="audit-table-empty">
                  {loading ? "Loading movements..." : "No movements match these filters."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 py-3 border-t border-[#E4E7EC] flex items-center justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={table.page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="audit-prev-page">
            <CaretLeft size={13} className="mr-1" />Previous
          </Button>
          <span className="text-xs text-[#667085]" data-testid="audit-page-indicator">Page {table.page} of {table.page_count}</span>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={table.page >= table.page_count}
            onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page">
            Next<CaretRight size={13} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InventoryMovementAuditDashboard;

const downloadDetailCsv = (entry) => {
  const v = entry.valuation || {};
  const header = ["Material", "Description", "SAP item", "Quantity", "UoM", "Amount", "Currency", "G/L account", "Cost centre"];
  const items = (v.items || []).map((i) => [
    i.material_id, i.material_description, i.material_document_item, i.quantity, i.uom,
    i.amount, i.currency, `${i.gl_account} ${i.gl_account_name}`, i.cost_center_id,
  ]);
  const lines = (entry.lines || []).map((l) => [l.product_id, l.description || "", "", l.quantity, l.quantity_uom, "", "", "", entry.cost_center_id || ""]);
  const rows = [
    [`Submission ${entry.id}`], [`Site`, entry.site_id], [`SAP GAC ID`, entry.sap_gac_id || ""],
    [`SAP document`, (v.material_document_numbers || []).join(" ")], [`Document year`, v.material_document_year || ""],
    [`SAP posting date`, v.posting_date || ""], [`SAP value`, v.status === "retrieved" ? v.total_amount : v.status],
    [], ["Lines submitted"], header, ...lines, [], ["SAP accounting items"], header, ...items,
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `Movement_${entry.sap_gac_id || entry.id}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const SapValuationBlock = ({ entry, onRefresh, refreshing }) => {
  const valuation = entry?.valuation;
  const status = valuation?.status || "pending";
  const badge = VALUATION_BADGE[status] || VALUATION_BADGE.pending;
  const items = valuation?.items || [];
  return (
    <div className="border border-[#E4E7EC] rounded-lg p-3 space-y-2" data-testid="detail-sap-valuation">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold text-[#101828]">SAP financial value</p>
          <Badge variant="outline" className={`text-[10px] ${badge.className}`} data-testid="detail-valuation-badge">{badge.label}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => downloadDetailCsv(entry)} data-testid="detail-download-btn">
            <DownloadSimple size={12} className="mr-1" />Download
          </Button>
          {!!entry?.sap_gac_id && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRefresh} disabled={refreshing} data-testid="detail-refresh-valuation-btn">
              {refreshing ? <CircleNotch size={12} className="animate-spin mr-1" /> : <ArrowsClockwise size={12} className="mr-1" />}Refresh from SAP
            </Button>
          )}
        </div>
      </div>

      {status === "retrieved" && (
        <>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-[#667085]">SAP document: </span>
              <button
                className="font-mono font-bold text-[#101828] hover:text-[#0E7C86]"
                onClick={() => { navigator.clipboard.writeText((valuation.material_document_numbers || []).join(", ")); toast.success("SAP document number copied"); }}
                data-testid="detail-copy-document-btn"
              >
                {(valuation.material_document_numbers || []).join(", ") || "—"}
              </button>
            </div>
            <div><span className="text-[#667085]">Document year: </span><span className="font-mono">{valuation.material_document_year || "—"}</span></div>
            <div><span className="text-[#667085]">SAP posting date: </span>{valuation.posting_date || "—"}</div>
            <div><span className="text-[#667085]">Accounting items: </span>{valuation.item_count}</div>
            <div className="col-span-2">
              <span className="text-[#667085]">Movement value: </span>
              <span className="font-bold text-[#101828]" data-testid="detail-valuation-amount">{money(valuation.total_amount_abs, valuation.currency)}</span>
              <span className="text-[#667085]"> ({valuation.total_amount < 0 ? "credit to inventory" : "debit to inventory"})</span>
            </div>
          </div>
          {valuation.reversal && (
            <p className="text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-md px-2 py-1" data-testid="detail-reversal-note">
              SAP holds a reversal/cancellation entry for this document - the value above is the net of all its accounting items.
            </p>
          )}
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px]">Material</TableHead>
                <TableHead className="text-[11px]">Item</TableHead>
                <TableHead className="text-[11px] text-right">Quantity</TableHead>
                <TableHead className="text-[11px] text-right">Value</TableHead>
                <TableHead className="text-[11px]">G/L account</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={`${item.material_document_number}-${item.material_document_item}-${i}`} data-testid={`detail-valuation-item-${i}`}>
                  <TableCell className="text-xs">
                    <span className="font-mono font-semibold">{item.material_id}</span>
                    <span className="block text-[10px] text-[#667085]">{item.material_description}</span>
                  </TableCell>
                  <TableCell className="text-xs font-mono">{item.material_document_item}</TableCell>
                  <TableCell className="text-xs text-right">{qty(item.quantity)} {item.uom}</TableCell>
                  <TableCell className="text-xs text-right font-semibold">{money(item.amount, item.currency)}</TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono">{item.gl_account}</span>
                    <span className="block text-[10px] text-[#667085]">{item.gl_account_name}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      {status === "pending_authorisation" && (
        <p className="text-[11px] text-[#B54708]" data-testid="detail-valuation-pending-auth">
          SAP has not authorised this integration to read financial data for {entry.site_id}&apos;s company, so no amount can be
          retrieved for this movement. Ask your SAP key user to extend the integration user&apos;s financial-data access to that set of
          books - nothing is estimated here, so no value is shown rather than a misleading zero.
        </p>
      )}
      {status === "no_valuation" && (
        <p className="text-[11px] text-[#475467]" data-testid="detail-valuation-none">
          SAP posted no accounting value for this movement - typically a material that is not valuated, or a period SAP no longer
          exposes through its financial reports.
        </p>
      )}
      {(status === "error" || status === "mixed_currency") && (
        <p className="text-[11px] text-[#B42318]" data-testid="detail-valuation-error">
          {valuation?.last_error || "This document carries more than one currency, so it is excluded from the dashboard totals."}
        </p>
      )}
      {status === "pending" && (
        <p className="text-[11px] text-[#475467]" data-testid="detail-valuation-not-fetched">
          The SAP value for this movement has not been read yet. Use &quot;Refresh from SAP&quot; to fetch it now.
        </p>
      )}
    </div>
  );
};
